import * as Models from './../../Models/SharedMaterials/DataModel';
import { Action, Reducer } from 'redux';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from './../../';

import { ToxnotServiceClient } from './../../ToxnotServiceClient';
import { AppServices } from './../../../AppServices';
import UpdateCompanyRequest from '../../Models/Api/UpdateCompanyRequest';
import { ReportTypeWithContext } from '../../Models/Api/CustomerApiRequestsAndResponses';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';

// ===============================================================================

//
// State definitions
//

export enum ManagementOperationBaseStates {
    Default,
    OperationStarted,
    OperationCompleted,
    OperationFailed
}

export enum PublishingState {
    NoPublishing,
    Editing,
    Publishing,
    PublishedSuccessfully,
    PublishFailed
}

export enum CustomUrlNameState {
    Default,
    CheckingCustomName,
    CustomNameValid,
    CustomNameInvalid,
    CustomNameBlank
}

export interface AccessRequestInboxState {
    isLoading: boolean;
    catalog: Models.SharedMaterialsAccessRequestCatalog;
    respondingToRequestsState: ManagementOperationBaseStates;
}

export interface AccessManagerState {
    isLoading: boolean;
    bundle: Models.AccessManagerBundle;
    currentlyShowACLForSharedMaterialId: string | null;
    currentlySelectedSharedMaterialIds: { [sharedMaterialId: string]: string };
    grantingState: ManagementOperationBaseStates;
    removingFromCatalogs: boolean;
}

export interface ManagementState {
    ownedSharedMaterialsMap: { [sharedMaterialId: string]: Models.SharedMaterialCoreData },
    loading: boolean;
    accessRequestInboxState: AccessRequestInboxState;
    accessManagerState: AccessManagerState;
}


// ===============================================================================

//
// Action definitions
//

const RequestAccessRequestsValue = "REQUEST_ACCESS_REQUESTS"

interface RequestAccessRequestsAction {
    type: typeof RequestAccessRequestsValue;
}

const ReceiveAccessRequestsValue = "RECEIVE_ACCESS_REQUESTS"

interface ReceiveAccessRequestsAction {
    type: typeof ReceiveAccessRequestsValue;
    catalog: Models.SharedMaterialsAccessRequestCatalog;
}

const RespondAccessRequestValue = "RESPOND_ACCESS_REQUEST";

interface RespondAccessRequestAction {
    type: typeof RespondAccessRequestValue;
    state: ManagementOperationBaseStates;
    currentRequests: Models.SharedMaterialsAccessRequestCatalogRequest[];
}

const UpdateAccessRequestStateValue = "UPDATE_ACCESS_REQUEST_STATE";

interface UpdateAccessRequestStateAction {
    type: typeof UpdateAccessRequestStateValue;
}

const RequestAccessManagerDataValue = "REQUEST_ACCESS_MANAGER_DATA_VALUE";

interface RequestAccessManagerDataAction {
    type: typeof RequestAccessManagerDataValue;
}

const ReceiveAccessManagerDataValue = "RECEIVE_ACCESS_MANAGER_DATA_VALUE";

interface ReceiveAccessManagerDataAction {
    type: typeof ReceiveAccessManagerDataValue;
    bundle: Models.AccessManagerBundle;
}

const SetActiveAccessControlListValue = "SET_ACTIVE_ACCESS_CONTROL_LIST";

interface SetActiveAccessControlListAction {
    type: typeof SetActiveAccessControlListValue;
    sharedMaterialId: string | null;
}

const SetAccessControlListValue = "SET_ACCESS_CONTROL_LIST";

interface SetAccessControlListAction {
    type: typeof SetAccessControlListValue;
    currentAclMap: { [sharedMaterialId: string]: Models.SharedMaterialsAccessControlListInfo[] };
}

const GrantingGroupAccessValue = "GRANTING_GROUP_ACCESS";

interface GrantingGroupAccessAction {
    type: typeof GrantingGroupAccessValue;
}

const GrantedGroupAccessValue = "GRANTED_GROUP_ACCESS";

interface GrantedGroupAccessAction {
    type: typeof GrantedGroupAccessValue;
    state: ManagementOperationBaseStates;
}

const UpdateSelectedSharedMaterialsForACLValue = "UPDATED_SELECTED_SHARED_MATERIALS_FOR_ACL";

interface UpdateSelectedSharedMaterialsForACLAction {
    type: typeof UpdateSelectedSharedMaterialsForACLValue;
    selectedIds: { [sharedMaterialId: string]: string };
}

const ResetGrantingStateValue = "RESET_GRANTING_STATE";

interface ResetGrantingStateAction {
    type: typeof ResetGrantingStateValue
}

const UpdateManagementStateValue = "UPDATE_MANAGEMENT_STATE";

interface UpdateManagementStateAction {
    type: typeof UpdateManagementStateValue;
    value: Partial<ManagementState>;
}

// ===============================================================================

//
// Helper functions
//


async function dispatchGetAllAccessManagagerDataAsync(state: ApplicationState, dispatch: (action: KnownAction) => void) {
    if (!state || !state.sharedMaterialsManagementState || !state.sharedMaterialsManagementState.accessManagerState || !state.appServices) {
        return;
    }

    dispatch({ type: RequestAccessManagerDataValue });

    const apiResult = await toxnotServiceClient.sharedMaterialsGetAccessManagerBundle();

    if (!apiResult.success || !apiResult.data) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    dispatch({type: ReceiveAccessManagerDataValue, bundle: apiResult.data})
}

async function dispatchRemoveFromAccessControlListAsync(state: ApplicationState, dispatch: (action: KnownAction) => void, teamOwnerIdWithAccess: string, sharedMaterialId: string) {
    if (!state || !state.sharedMaterialsManagementState || !state.sharedMaterialsManagementState.accessManagerState || !state.appServices) {
        return;
    }

    const apiResult = await toxnotServiceClient.removeFromAccessControlList(teamOwnerIdWithAccess, sharedMaterialId);

    if (!apiResult.success) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    let currentAclMap = state.sharedMaterialsManagementState.accessManagerState.bundle.accessControlListMap;
    currentAclMap[sharedMaterialId] = currentAclMap[sharedMaterialId].filter(t => t.teamOwnerIdWithAccess !== teamOwnerIdWithAccess);

    dispatch({ type: SetAccessControlListValue, currentAclMap: currentAclMap})
}

async function dispatchGrantGroupAccessToSharedMaterialsAsync(state: AccessManagerState, dispatch: (action: KnownAction) => void, emailAddresses: string[], sharedMaterialIds: string[]) {
    if (!state) {
        return;
    }

    dispatch({type: GrantingGroupAccessValue})

    const apiResult = await toxnotServiceClient.grantGroupAccess({ emailAddressesToGrantAccess: emailAddresses, sharedMaterialIds: sharedMaterialIds });

    dispatch({ type: GrantedGroupAccessValue, state: !apiResult.success ? ManagementOperationBaseStates.OperationFailed : ManagementOperationBaseStates.OperationCompleted });
}

export function testCustomUrlName(input: string) {
    // regex
    const pattern = /^[a-zA-Z0-9_\-]*$/;
    return pattern.test(input);
}

// ===============================================================================

//
// Action creators
//

type KnownAction = 
    RequestAccessRequestsAction |
    ReceiveAccessRequestsAction |
    RespondAccessRequestAction |
    UpdateAccessRequestStateAction |
    RequestAccessManagerDataAction |
    ReceiveAccessManagerDataAction |
    SetActiveAccessControlListAction |
    SetAccessControlListAction |
    UpdateSelectedSharedMaterialsForACLAction |
    GrantedGroupAccessAction |
    GrantingGroupAccessAction |
    ResetGrantingStateAction |
    UpdateManagementStateAction;

// Service Layer API Client.
const toxnotServiceClient = new ToxnotServiceClient();

const defaultAccessRequestInboxState: AccessRequestInboxState = {
    isLoading: true,
    catalog: {
        accessRequests: [],
        allSharedMaterialsCoreDataMap: {}
    },
    respondingToRequestsState: ManagementOperationBaseStates.Default,
}

const defaultAccessManagerState: AccessManagerState = {
    isLoading: true,
    bundle: {
        accessControlListMap: {},
        numberOfActiveAccessRequests: 0
    },
    currentlyShowACLForSharedMaterialId: null,
    currentlySelectedSharedMaterialIds: {},
    grantingState: ManagementOperationBaseStates.Default,
    removingFromCatalogs: false
}

export const actionCreators = {
    loadManagementState: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsManagementState) {
            return;
        }

        const apiResult = await toxnotServiceClient.sharedMaterialsGetForTeam();

        if (!apiResult.success || apiResult.data === null) {
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        dispatch({
            type: UpdateManagementStateValue,
            value: {
                ownedSharedMaterialsMap: apiResult.data,
                loading: false
            }
        });
    },
    getAllAccessRequests: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsManagementState) {
            return;
        }

        toxnotServiceClient.getSharedMaterialAccessRequests()
            .then(apiResult => {
                dispatch({ type: ReceiveAccessRequestsValue, catalog: apiResult.data ?? defaultAccessRequestInboxState.catalog });
            });

        dispatch({ type: RequestAccessRequestsValue });
    },
    respondToAccessRequest: (requestId: string, type: Models.AccessRequestResponseType): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsManagementState) {
            return;
        }

        const inboxState = appState.sharedMaterialsManagementState.accessRequestInboxState;

        if (!inboxState || !inboxState.catalog) {
            return;
        }

        const currentRequests = inboxState.catalog.accessRequests.filter(r => r.requestId !== requestId);

        toxnotServiceClient.respondToSharedMaterialAccessRequest(requestId, type)
            .then(apiResult => {
                dispatch({
                    type: RespondAccessRequestValue, state: apiResult.success ? ManagementOperationBaseStates.OperationCompleted : ManagementOperationBaseStates.OperationFailed,
                    currentRequests: currentRequests
                });
            });

        dispatch({ type: UpdateAccessRequestStateValue });
    },
    getAllAccessManagerData: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        dispatchGetAllAccessManagagerDataAsync(appState, dispatch);
    },
    showAccessControlList: (sharedMaterialId: string | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsManagementState) {
            return;
        }

        dispatch({ type: SetActiveAccessControlListValue, sharedMaterialId: sharedMaterialId })
    },
    removeFromAccessControlList: (teamOwnerIdWithAccess: string, sharedMaterialId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        dispatchRemoveFromAccessControlListAsync(appState, dispatch, teamOwnerIdWithAccess, sharedMaterialId);
    },
    updateSelectedMaterials: (selectedIds: { [id: string]: string }): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsManagementState) {
            return;
        }

        dispatch({ type: UpdateSelectedSharedMaterialsForACLValue, selectedIds: selectedIds });
    },
    grantGroupAccessToSharedMaterials: (emailAddresses: string[], sharedMaterialIds: string[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsManagementState) {
            return;
        }

        dispatchGrantGroupAccessToSharedMaterialsAsync(appState.sharedMaterialsManagementState.accessManagerState, dispatch, emailAddresses, sharedMaterialIds);
    },
    resetGrantingState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.sharedMaterialsManagementState) {
            return;
        }

        dispatch({ type: ResetGrantingStateValue });
    },
    removeSharedMaterialsFromCatalogs: (sharedMaterialIds: string[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        if (!appState?.sharedMaterialsManagementState?.accessManagerState) {
            return;
        }

        dispatch({
            type: UpdateManagementStateValue,
            value: {
                ...appState.sharedMaterialsManagementState,
                accessManagerState: {
                    ...appState.sharedMaterialsManagementState.accessManagerState,
                    removingFromCatalogs: true
                }
            }
        });

        const apiResult = await toxnotServiceClient.sharedMaterialsUpdateFetchForCatalogs({sharedMaterialIdsToRemoveFromCatalogs: sharedMaterialIds, fetchForCatalogs: false});

        if (!apiResult.success) {
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        const newSharedMaterialsMap = { ...appState.sharedMaterialsManagementState.ownedSharedMaterialsMap };

        for (let id of sharedMaterialIds) {
            newSharedMaterialsMap[id].fetchForCatalogs = false;
        }

        dispatch({
            type: UpdateManagementStateValue,
            value: {
                ownedSharedMaterialsMap: newSharedMaterialsMap,
                accessManagerState: {
                    ...appState.sharedMaterialsManagementState.accessManagerState,
                    removingFromCatalogs: false
                }
            }
        });
    },
    deletePassports: (sharedMaterialIds: string[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        if (!appState?.sharedMaterialsManagementState?.accessManagerState) {
            return;
        }

        dispatch({
            type: UpdateManagementStateValue,
            value: {
                ...appState.sharedMaterialsManagementState,
                accessManagerState: {
                    ...appState.sharedMaterialsManagementState.accessManagerState,
                    isLoading: true
                }
            }
        });

        const publicInventoryIds = Object.
            values(appState.sharedMaterialsManagementState.ownedSharedMaterialsMap).
            filter(s => sharedMaterialIds.includes(s.sharedMaterialId)).
            map(s => s.publicInventoryId);

        const result = await toxnotServiceClient.publicationsDelete(publicInventoryIds);

        if (!result.success) {
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        const newOwnedMap = { ...appState.sharedMaterialsManagementState.ownedSharedMaterialsMap };
        const newSelectedMap = { ...appState.sharedMaterialsManagementState.accessManagerState.currentlySelectedSharedMaterialIds };

        for (let id of sharedMaterialIds) {
            delete newOwnedMap[id];
            delete newSelectedMap[id];
        }

        dispatch({
            type: UpdateManagementStateValue,
            value: {
                ...appState.sharedMaterialsManagementState,
                ownedSharedMaterialsMap: newOwnedMap,
                accessManagerState: {
                    ...appState.sharedMaterialsManagementState.accessManagerState,
                    isLoading: false,
                    currentlyShowACLForSharedMaterialId: null,
                    currentlySelectedSharedMaterialIds: newSelectedMap
                }
            }
        });
    }
};

// ===============================================================================

//
// Reducers
//

const initialState: ManagementState = {
    ownedSharedMaterialsMap: {},
    loading: true,
    accessRequestInboxState: defaultAccessRequestInboxState,
    accessManagerState: defaultAccessManagerState
}

export const reducer: Reducer<ManagementState> = (state: ManagementState | undefined, incomingAction: Action): ManagementState => {
    if (state === undefined) {
        return initialState;
    }

    switch (incomingAction.type) {
        // does a generic update of the state.
        case UpdateManagementStateValue:
            return {
                ...state,
                ...(incomingAction as UpdateManagementStateAction).value
            }
        case RequestAccessRequestsValue:
            return {
                ...state,
                accessRequestInboxState: {
                    ...state.accessRequestInboxState,
                    respondingToRequestsState: ManagementOperationBaseStates.Default,
                    isLoading: true
                }
            }
        case ReceiveAccessRequestsValue:
            const receiveAction = incomingAction as ReceiveAccessRequestsAction

            return {
                ...state,
                accessRequestInboxState: {
                    ...state.accessRequestInboxState,
                    isLoading: false,
                    catalog: receiveAction.catalog
                }
            }
        case UpdateAccessRequestStateValue:
            return {
                ...state,
                accessRequestInboxState: {
                    ...state.accessRequestInboxState,
                    respondingToRequestsState: ManagementOperationBaseStates.OperationStarted
                }
            }
        case RespondAccessRequestValue:
            const respondAccessRequestAction = incomingAction as RespondAccessRequestAction;

            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    bundle: {
                        ...state.accessManagerState.bundle,
                        numberOfActiveAccessRequests: respondAccessRequestAction.currentRequests.length
                    }
                },
                accessRequestInboxState: {
                    ...state.accessRequestInboxState,
                    catalog: {
                        allSharedMaterialsCoreDataMap: state.accessRequestInboxState.catalog!.allSharedMaterialsCoreDataMap,
                        accessRequests: respondAccessRequestAction.currentRequests
                    },
                    respondingToRequestsState: respondAccessRequestAction.state
                }
            }

        case RequestAccessManagerDataValue:
            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    isLoading: true
                }
            };
        case ReceiveAccessManagerDataValue:
            const receiveManagerDataAction = incomingAction as ReceiveAccessManagerDataAction;

            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    isLoading: false,
                    bundle: receiveManagerDataAction.bundle
                }
            };
        case SetActiveAccessControlListValue:
            const showAccessControlListAction = incomingAction as SetActiveAccessControlListAction;

            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    currentlyShowACLForSharedMaterialId: showAccessControlListAction.sharedMaterialId
                }
            };
        case SetAccessControlListValue:
            const setAccessControlListAction = incomingAction as SetAccessControlListAction;

            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    bundle: {
                        ...state.accessManagerState.bundle,
                        accessControlListMap: setAccessControlListAction.currentAclMap
                    },
                }
            };
        case UpdateSelectedSharedMaterialsForACLValue:
            const sharedMaterialIdsForACLAction = incomingAction as UpdateSelectedSharedMaterialsForACLAction;
            
            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    currentlySelectedSharedMaterialIds: sharedMaterialIdsForACLAction.selectedIds
                }
            };
        case GrantedGroupAccessValue:
            const grantGroupAccessAction = incomingAction as GrantedGroupAccessAction;
            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    grantingState: grantGroupAccessAction.state
                }
            };
        case ResetGrantingStateValue:
            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    grantingState: ManagementOperationBaseStates.Default
                }
            };
        case GrantingGroupAccessValue:
            return {
                ...state,
                accessManagerState: {
                    ...state.accessManagerState,
                    grantingState: ManagementOperationBaseStates.OperationStarted
                }
            };
    }

    return state;
}

